import { fn } from '@ember/helper';
import { on } from '@ember/modifier';
import { action } from '@ember/object';
import type Owner from '@ember/owner';
import { next } from '@ember/runloop';
import { service } from '@ember/service';
import { tracked } from '@glimmer/tracking';

import type { PopupData, Service } from 'webapp-types';
import { TrackingEvent } from 'webapp/enums/tracking';

import eq from 'webapp/helpers/eq';
import makeArrayHelper from 'webapp/helpers/make-array';
import CommonUiIconComponent from 'webapp/components/common/ui-icon';
import type { Args } from 'webapp/components/popup/inner/modal';
import PopupInnerModalComponent from 'webapp/components/popup/inner/modal';
import SubmitButton from 'webapp/components/submit-button';

interface StepItem {
	icon: IconName;
	text: string;
}

interface Step {
	id: number;
	label: string;
	title: string;
	description: string;
	items: StepItem[];
}

export default class OnboardingPopupNewNavigation extends PopupInnerModalComponent<PopupData.None> {
	@service('analytics') declare analyticsService: Service.Analytics;
	@service('navigation') declare navigationService: Service.Navigation;
	@service('user-settings') declare userSettingsService: Service.UserSettings;

	@tracked selectedIndex = 0;

	steps: Step[] = [
		{
			id: 1,
			label: 'NEW',
			title: 'Explore Databox’ new navigation',
			description: 'A more natural way to move between key pillars of our platform.',
			items: [
				{
					icon: 'Genie',
					text: 'Agentic AI',
				},
				{
					icon: 'DatablockLibrary',
					text: 'Reporting & analytics',
				},
				{
					icon: 'DataManager',
					text: 'Data manager',
				},
			],
		},
		{
			id: 2,
			label: 'AGENTIC ANALYTICS',
			title: 'Get insights and build anything with just a prompt',
			description:
				'Ask a question about your performance. Tell Genie what you want to track or visualize.',
			items: [
				{
					icon: 'AiSummary',
					text: 'Analyze performance instantly',
				},
				{
					icon: 'Blocks',
					text: 'Create metrics, dashboards & artifacts',
				},
				{
					icon: 'Share',
					text: 'Share insights with anyone',
				},
			],
		},
		{
			id: 3,
			label: 'REPORTING & ANALYTICS',
			title: 'Create dashboards, automate reports, improve performance',
			description:
				'Drag and drop your metrics onto dashboards and reports or view metric insights to dive deeper.',
			items: [
				{
					icon: 'MetricDetail',
					text: 'Metrics & KPIs',
				},
				{
					icon: 'DatablockLibrary',
					text: 'Dashboards & reports',
				},
				{
					icon: 'OkRs',
					text: 'Goals and OKRs',
				},
			],
		},
		{
			id: 4,
			label: 'DATA MANAGER',
			title: 'Your data in one place',
			description:
				'Centralize all your data in one place to uncover insights from a trusted, governed data foundation.',
			items: [
				{
					icon: 'Connect',
					text: 'Connect your data',
				},
				{
					icon: 'DataSettings',
					text: 'Manage data sources',
				},
				{
					icon: 'Table',
					text: 'Create datasets',
				},
			],
		},
	];

	get themeId() {
		return this.navigationService.theme.id;
	}

	get currentStep() {
		return this.steps[this.selectedIndex];
	}

	get nextBtnText() {
		return this.selectedIndex === this.steps.length - 1 ? "OK, let's try it out" : 'Next';
	}

	constructor(owner: Owner, args: Args) {
		super(owner, args);

		next(() => {
			this.userSettingsService.hasSeenOnboardingNewNavigationPopup = true;
			this.analyticsService.event(TrackingEvent.NEW_NAVIGATION_INTRO_MODAL_SHOWN);
		});
	}

	@action
	selectStep(index: number) {
		this.selectedIndex = index;
	}

	@action
	back() {
		if (this.selectedIndex === 0) {
			return;
		}

		this.selectedIndex--;
	}

	@action
	next() {
		if (this.selectedIndex === this.steps.length - 1) {
			this.analyticsService.event(TrackingEvent.NEW_NAVIGATION_INTRO_MODAL_CTA_CLICKED);
			this.close();

			return;
		}

		this.selectedIndex++;
	}

	<template>
		<div class="content">
			<div class="content__left">
				<div class="content__left-inner">
					<div class="text">
						<div class="text__header">
							<span class="badge2 badge2--blue">{{this.currentStep.label}}</span>
							<h3>{{this.currentStep.title}}</h3>
						</div>
						<p>{{this.currentStep.description}}</p>
					</div>
					<ul>
						{{#each this.currentStep.items as |item|}}
							<li>
								<CommonUiIconComponent @name={{item.icon}} @size="md" />
								<span>{{item.text}}</span>
							</li>
						{{/each}}
					</ul>
				</div>

				<div class="controls2">
					<div class="controls2__left">
						{{#unless (eq this.selectedIndex 0)}}
							<SubmitButton
								@text="Back"
								@theme="grey-outlined"
								@onTrigger={{this.back}}
							/>
						{{/unless}}

						<SubmitButton @text={{this.nextBtnText}} @onTrigger={{this.next}} />
					</div>
				</div>
			</div>
			<div class="content__right">
				<img
					src="/images/onboarding/new-navigation/{{this.currentStep.id}}-{{this.themeId}}.jpg"
					alt=""
				/>
				<ul class="current-step-indicator">
					{{#each (makeArrayHelper length=this.steps.length) as |_item index|}}
						<li
							role="button"
							class={{if (eq this.selectedIndex index) "selected" ""}}
							{{on "click" (fn this.selectStep index)}}
						></li>
					{{/each}}
				</ul>
			</div>
		</div>
	</template>
}
